import { useState } from 'react';

import Tippy from '@tippyjs/react';

import { cn, ia } from 'lib/helpers/utility';

import Avatar from '../Avatar/Avatar';

import AllAvatars from './components/AllAvatars';
import { getClasses } from './lib/getClasses';
import { getFirstName, getLastName } from './lib/getName';

/**
 * @param {import('./lib/propTypes').AvatarGroupProps} AvatarGroupProps
 */

const AvatarGroup = ({
  items = [],
  itemsToShow = 4,
  size = 'medium',
  isBorder = true,
  label = '',
  className,
  tooltipSinglePlacement = 'bottom',
  popoverPosition = 'bottom-end',
  itemsGroupBy = '',
  itemsGroupLabelClassName,
  isDisabled = false
}) => {
  const [isAllAvatarsOpen, setIsAllAvatarsOpen] = useState(false);
  const classes = getClasses(size, isBorder);
  const displayedItems = items.slice(0, itemsToShow);

  if (!ia(items)) return null;

  const getTooltipContent = (item) => `${getFirstName(item)} ${getLastName(item)}`.trim();

  return (
    <div
      className={cn(
        'relative flex w-min select-none items-center rounded-full bg-white p-[2px] focus-within:bg-neutral-200 hover:bg-neutral-100',
        `h-[${classes.size + 6}px]`,
        !isBorder && 'shadow-[0_2px_6px_rgba(65,70,81,0.08)]',
        isAllAvatarsOpen && '!bg-neutral-200',
        classes.wrapper,
        className
      )}>
      {label && (
        <label
          className={cn(
            'font-500 capitalize leading-none',
            size === 'small' || size === 'medium' ? 'mr-1' : 'mr-[6px]',
            isDisabled ? 'text-neutral-400' : 'text-neutral-700',
            classes.label,
            classes.labelMargin
          )}>
          {label}
        </label>
      )}
      <div className={cn('flex', isDisabled && 'opacity-40')}>
        {displayedItems.map((item, index) => (
          <div
            key={item.id}
            className="relative"
            style={{
              marginLeft: index > 0 && -classes.size / 4,
              zIndex: index + 1
            }}>
            <Tippy
              className="tippy-dark"
              animation="fade"
              placement={tooltipSinglePlacement}
              arrow
              disabled={isDisabled || !getTooltipContent(item)}
              content={getTooltipContent(item)}>
              <div>
                <Avatar
                  size={size}
                  fontSize={classes.itemFontSize}
                  firstName={getFirstName(item)}
                  lastName={getLastName(item)}
                  image={item.image}
                  isSquare={false}
                  id={item.id}
                  className="!outline outline-2 outline-white"
                />
              </div>
            </Tippy>
          </div>
        ))}
      </div>
      <AllAvatars
        items={items}
        size={size}
        itemSize={classes.size}
        itemsToShow={itemsToShow}
        fontSize={classes.label}
        itemFontSize={classes.itemFontSize}
        placement={popoverPosition}
        itemsGroupBy={itemsGroupBy}
        itemsGroupLabelClassName={itemsGroupLabelClassName}
        isDisabled={isDisabled}
        onShow={() => setIsAllAvatarsOpen(true)}
        onHide={() => setIsAllAvatarsOpen(false)}
      />
    </div>
  );
};

export default AvatarGroup;
